import { BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, useBCMSContext, useModel, useRoute, useStateManager } from "@bcm-dev/bcms-vite";
import Classes, { MobileDevice } from "model";

import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { InfoBanner } from "components/shared/views/ConfirmBannerView";
import { PrimaryButton, UITextInput } from "components/ui";
import UIPasswordInput from "components/ui/UIPasswordInput";

import "./styles.css";

/**
 * @function AuthorizedDeviceEnrollmentPage
 * @param {OBJECT} props - Properties for the component.
 * @returns {React.Component}
 * @description A page for the device enrollment workflow.
 */
export default function AuthorizedDeviceEnrollmentPage(props) {
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, globalController);

    const data = { ...getDefaultPageData(), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };
    const className = props.className?(" "+props.className):"";
    const navigate = useRoute();

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update([ "data", "mobile", prop ], value)
                .submit(setState);
        },

        onSubmit: evt => {
            const device = MobileDevice.init({ ...state.data.mobile.record, isEnrolled: true });
            const defaultDevice = BCMSDefaultModel.mobileDevice();
            const merge = BCMSDefaultModel.mergeRecords(device, defaultDevice);
            const newDevice = MobileDevice.init(merge);

            MobileDevice.create(newDevice)
                .submit(Classes);

            BCMSStateManagerCRUD.init(state)    
                .update([ "data", "mobile" ], newDevice)
                .submit(setState);
        },

        onViewEnrolledDevices: evt => {
            navigate(`/authorizedDevices`);
        },

        onClose: evt => {
          BCMSStateManagerCRUD.init(state)
            .update([ "ui", "confirmationBanner", "isActive" ], false)
            .submit(setState);
        }
    });

    return (
        <PageContainer className={"authorized-device-enrollment-page"+className} state={ { data, ui } } controller={ controller }>
            <AuthorizedDeviceEnrollmentContent />
        </PageContainer>
    );
}

/**
 * @function getDefaultPageData
 * @param {VOID} - No parameters for now.
 * @returns {React.Component}
 * @description Default data for the page.
 */
export const getDefaultPageData = () => ({
    mobile: MobileDevice.init({})
});

/**
 * @function getDefaultPageUI
 * @param {VOID} - No parameters for now.
 * @returns {React.Component}
 * @description Default ui for the page.
 */
export const getDefaultPageUI = () => ({
    confirmationBanner: { isActive: true }
});

/**
 * @function AuthorizedDeviceEnrollmentContent
 * @param {OBJECT} props - Properties for the component.
 * @returns {React.Component}
 * @description A component for the page content.
 */
function AuthorizedDeviceEnrollmentContent(props) {
    const { data, ui, onChange, onSubmit, onViewEnrolledDevices, onClose } = useStateManager();
    const { branding } = useModel();
    const { options } = branding;
    const hasBiometricManagement = options.biometric_management_abi22 === "true";
    const device = data.mobile;
    const isEnrolled = device.isEnrolled;

    return (
        <PageContentView>
            <div className="flex-container flex-vertical flex-width">
                { isEnrolled && !hasBiometricManagement && <EnrolledContent/> }
                { !isEnrolled && <UnenrolledContent/> }
                { isEnrolled && hasBiometricManagement && <BiometricManagementContent/> }
            </div>
        </PageContentView>
    );
}

/**
 * @function EnrolledContent
 * @param {OBJECT} props - Properties for the component.
 * @returns {React.Component}
 * @description Enrolled content for the page.
 */
function EnrolledContent(props) {
    const { data, ui, onChange, onClose } = useStateManager();
    const device = data.mobile;

    return (
        <>
            { ui.confirmationBanner.isActive &&
                <InfoBanner text="This device has been enrolled for biometric authentication." onClick={ onClose } dataPoint="Device_Enrollment_Banner" /> }
            
            <CenterContentContainer className="authorized-device-enrollment">
                <ContentHeaderView text="Authorized Device Enrollment" />
                
                <div className="content-container">
                    <p>Unenrolling will disable biometric authentication on this device.</p>
                
                </div>
                
                <FormContainer className="authorized-device-enrollment-form">
                    <FormRowView text="Device Name (e.g. Mary's Phone)">
                        <UITextInput value={ device.name } disabled={ true } onChange={ onChange("name") } />
                    </FormRowView>
                    <FormButtonRow>
                        <DisabledPrompt>
                            <PrimaryButton text="Unenroll" dataPoint="Device_Enrollment_Unenroll" />
                        </DisabledPrompt>
                    </FormButtonRow>
                </FormContainer>
            </CenterContentContainer>
        </>
    );
}

/**
 * @function EnrolledContent
 * @param {OBJECT} props - Properties for the component.
 * @returns {React.Component}
 * @description Unenrolled content for the page.
 */
function UnenrolledContent() {
        const { data, ui, onChange, onSubmit, onViewEnrolledDevices, onClose } = useStateManager();
    const device = data.mobile;
    
    return (
        <CenterContentContainer className="authorized-device-enrollment">
            <ContentHeaderView text="Authorized Device Enrollment" />
            
            <div className="content-container">
                <p>Unenrolling will disable biometric authentication on this device.</p>
            </div>
            
            <FormContainer className="authorized-device-enrollment-form">
                <FormRowView text="Device Name (e.g. Mary's Phone)">
                    <UITextInput dataPoint="Device_Enrollment_Name" value={ device.name } onChange={ onChange("name") } />
                </FormRowView>
                <FormRowView text="*User ID">
                    <UITextInput dataPoint="Device_Enrollment_User" value={ device.userID } onChange={ onChange("userID") } />
                </FormRowView>
                <FormRowView text="*Password">
                    <UIPasswordInput dataPoint="Device_Enrollment_Password" value={ device.password } onChange={ onChange("password") } />
                </FormRowView>
                <FormButtonRow>
                    <PrimaryButton dataPoint="Device_Enrollment_Enroll" text="Enroll" onClick={ onSubmit } />
                </FormButtonRow>
            </FormContainer>
        </CenterContentContainer>
    );
}

/**
 * @function BiometricManagementContent
 * @param {OBJECT} props - Properties for the component.
 * @returns {React.Component}
 * @description Biometric content for the page.
 */
function BiometricManagementContent() {
    const { onViewEnrolledDevices } = useStateManager();
    
    return (
        <>
            { <EnrolledContent/> }
            <CenterContentContainer className="authorized-device-enrollment">
                <ContentHeaderView className="authorized-device-enrollment-biometric-management-label-container" dataPoint="Device_Enrollment_Biometric_Management_Label" text="Biometric Management" />
                <FormContainer className="authorized-device-enrollment-form">
                    <FormButtonRow>
                        <PrimaryButton text="View Enrolled Devices"
                        dataPoint="Device_Enrollment_Biometric_Management_View_Enrolled_Devices_Button"
                        onClick={ onViewEnrolledDevices } />
                    </FormButtonRow>
                </FormContainer>
            </CenterContentContainer>
        </>
    );
}